import _ from "lodash";
import {
  COURSE_BASED_GRADING,
  SUBJECT_BASED_GRADING,
} from "Constants/stringConstants";
import { getAcademicSetupType } from "LearningCourses/modules/Utils";
import { getMemoizedFunction, generateNestedSelectedData } from "Utils";

export const ARCHIVAL_STATE_OPTIONS = [
  {
    id: "ALL",
    label: "common:all",
  },
  {
    id: "ACTIVE",
    label: "common:active",
  },
  {
    id: "ARCHIVED",
    label: "common:archived",
  },
];

export const FILTERS = {
  [COURSE_BASED_GRADING]: ["grades", "subjects", "tags", "archivalState"],
  [SUBJECT_BASED_GRADING]: ["grades", "tags", "archivalState"],
  null: ["grades", "tags", "archivalState"],
};

export const DEFAULT_STANDARD_FILTERS = {
  grades: [],
  subjects: [],
  tags: [],
  archivalState: "ACTIVE",
};

export const getLocalizedFilterLabels = t => ({
  grades: t("scopeAndSequence:grade_levels"),
  subjects: t("scopeAndSequence:subject_areas"),
  tags: t("common:tags"),
  archivalState: t("common:status"),
});

export const getFilterOverlayOptions = params => {
  const { t, grades, subjects, tags } = params;

  const localizedFilterLabels = getLocalizedFilterLabels(t);

  const academicSetupType = getAcademicSetupType();
  const filters = FILTERS[academicSetupType];

  return _.map(filters, filterKey => {
    switch (filterKey) {
      case "grades": {
        return {
          id: filterKey,
          label: localizedFilterLabels[filterKey],
          optionsType: "checkbox",
          filterOptions: _.map(grades, ({ id, name }) => ({
            id,
            label: name,
          })),
          includeAllOption: true,
          showSearch: true,
        };
      }
      case "subjects": {
        return {
          id: filterKey,
          label: localizedFilterLabels[filterKey],
          optionsType: "checkbox",
          filterOptions: _.map(subjects, ({ id, name }) => ({
            id,
            label: name,
          })),
          includeAllOption: true,
          showSearch: true,
        };
      }
      case "tags": {
        return {
          id: filterKey,
          label: localizedFilterLabels[filterKey],
          optionsType: "checkbox",
          filterOptions: tags,
          includeAllOption: true,
          showSearch: true,
        };
      }

      case "archivalState": {
        return {
          id: filterKey,
          label: localizedFilterLabels[filterKey],
          optionsType: "radio",
          filterOptions: _.map(ARCHIVAL_STATE_OPTIONS, item => ({
            ...item,
            label: t(item.label),
          })),
        };
      }
      default:
        return null;
    }
  });
};

export const getAllFiltersCount = ({ currentFilters }) => {
  return _.reduce(
    currentFilters,
    (acc, filterValue, filterKey) => {
      if (_.isEqual(filterValue, DEFAULT_STANDARD_FILTERS[filterKey])) {
        return acc;
      } else {
        return acc + 1;
      }
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterKey, filterValue }) => {
  const defaultFilterValue = DEFAULT_STANDARD_FILTERS[filterKey];

  if (_.isEqual(filterValue, defaultFilterValue)) {
    return 0;
  }
  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

const flattenChildrendNodes = ({ node, flattenedNodes }) => {
  flattenedNodes.push(node);
  if (node.children) {
    _.forEach(node.children, childNode => {
      if (childNode) {
        flattenChildrendNodes({ node: childNode, flattenedNodes });
      }
    });
  }
};

export const getFlattenedNodes = ({ nodes }) => {
  const flattenedNodes = [];
  _.forEach(nodes, node => {
    if (node) {
      flattenChildrendNodes({ node, flattenedNodes });
    }
  });
  return flattenedNodes;
};

export const getFilteredRootNodes = getMemoizedFunction(
  ({
    nodes,
    rootNodes,
    showSideNavigation,
    sideNavigationTab,
    isSubjectPropInNode,
  }) => {
    if (!showSideNavigation) {
      return rootNodes;
    }

    return _.filter(rootNodes, id => {
      const node = _.find(nodes, { id }) || {};
      if (isSubjectPropInNode) {
        return node?.subject == sideNavigationTab;
      }
      const associatedParents = _.get(node, "associatedParents", []);
      return _.find(associatedParents, { id: sideNavigationTab });
    });
  }
);

export const getFlattenedNestedNodes = getMemoizedFunction(
  ({
    nodes,
    rootNodes,
    value,
    sideNavigationTab,
    showSideNavigation,
    isSubjectPropInNode,
    isPlannerElement,
  }) => {
    if (!isPlannerElement) {
      return getFlattenedNodes({ nodes });
    }

    const filteredNodes = nodes;

    const filteredRootNodes = getFilteredRootNodes({
      nodes: filteredNodes,
      rootNodes,
      showSideNavigation,
      sideNavigationTab,
      isSubjectPropInNode,
    });

    const nestedNodes = generateNestedSelectedData({
      nodes: filteredNodes,
      rootNodes: filteredRootNodes,
      value,
    });

    return getFlattenedNodes({ nodes: nestedNodes });
  }
);
